import {
  endpoint,
  evaluation,
  formatMoney,
  formatTokens,
  models,
  type PageId,
  pages,
  projectContext,
  providerRoutes,
  type RequestRecord,
  readPage,
  requestRecords,
  traceSpans,
  usage,
  viewHref,
} from '@maas/core';
import '../components/tokens/tokens.css';
import '../components/button/button.css';
import '../components/tabs/tabs.css';
import '../components/data-table/data-table.css';
import '../components/dialog/dialog.css';
import '../components/composer/composer.css';
import './styles.css';
import { icon } from './icons';
import {
  type CatalogAccess,
  type CatalogModality,
  type DeployDraft,
  deployStepError,
  emptyDeployDraft,
  estimateHourlyCost,
  filterModels,
} from './state';

const appElement = document.querySelector<HTMLDivElement>('#app');
if (!appElement) throw new Error('Aster application root is missing.');
const app: HTMLDivElement = appElement;

let pageController = new AbortController();
let toastTimer = 0;
let lastDialogTrigger: HTMLElement | null = null;
const createdKeys: Array<{
  name: string;
  prefix: string;
  lastUsed: string;
  limit: number;
  project: string;
  environment: string;
}> = [];

const contextState: { organization: string; project: string; environment: string; region: string } =
  {
    organization: projectContext.organization,
    project: projectContext.project,
    environment: projectContext.environment,
    region: projectContext.region,
  };

const navItems: readonly {
  id: PageId;
  label: string;
  icon: Parameters<typeof icon>[0];
  group: string;
}[] = [
  { id: 'overview', label: 'Overview', icon: 'overview', group: 'Start' },
  { id: 'models', label: 'Models', icon: 'models', group: 'Discover' },
  { id: 'playground', label: 'Playground', icon: 'studio', group: 'Build' },
  { id: 'evaluations', label: 'Evaluations', icon: 'evaluate', group: 'Evaluate' },
  { id: 'endpoint', label: 'Endpoints', icon: 'endpoint', group: 'Deploy' },
  { id: 'requests', label: 'Requests', icon: 'requests', group: 'Operate' },
  { id: 'usage', label: 'Usage & keys', icon: 'usage', group: 'Operate' },
];

function routeLink(
  view: PageId,
  label: string,
  className = 'as-button',
  variant = 'secondary',
): string {
  return /* HTML */ `
    <a class="${className}" data-variant="${variant}" data-route href="${viewHref(view)}">
      ${label}
    </a>
  `;
}

function escapeHtml(value: string): string {
  return value.replace(
    /[&<>"']/g,
    (character) =>
      ({
        '&': '&amp;',
        '<': '&lt;',
        '>': '&gt;',
        '"': '&quot;',
        "'": '&#39;',
      })[character] ?? character,
  );
}

function renderShell(): void {
  let lastGroup = '';
  const navigation = navItems
    .map((item) => {
      const group =
        item.group === lastGroup
          ? ''
          : /* HTML */ `
              <span class="nav-group">${item.group}</span>
            `;
      lastGroup = item.group;
      return /* HTML */ `
        ${group}
        <a data-route data-nav-item="${item.id}" href="${viewHref(item.id)}">
          ${icon(item.icon)}
          <span>${item.label}</span>
        </a>
      `;
    })
    .join('');

  app.innerHTML = /* HTML */ `
    <div class="app-shell" data-nav-open="false">
      <aside id="product-navigation" class="app-rail" aria-label="Product navigation">
        <header class="rail-brand">
          <a data-route href="${viewHref('overview')}" aria-label="Aster MaaS home">
            <span class="aster-mark">✦</span>
            <strong>Aster</strong>
            <small>MaaS</small>
          </a>
          <button
            class="icon-button rail-close"
            type="button"
            data-mobile-nav-close
            aria-label="Close navigation"
          >
            ${icon('close')}
          </button>
        </header>
        <nav>${navigation}</nav>
        <div class="rail-foot">
          <span class="health-dot" aria-hidden="true"></span>
          <span>
            <b>All systems normal</b>
            <small>Shanghai · 42 ms</small>
          </span>
        </div>
      </aside>
      <button
        class="nav-scrim"
        type="button"
        data-mobile-nav-close
        aria-label="Close navigation"
      >
      </button>
      <div class="app-workspace">
        <header class="context-bar">
          <button
            class="icon-button mobile-nav-trigger"
            type="button"
            data-mobile-nav-trigger
            aria-label="Open navigation"
            aria-controls="product-navigation"
            aria-expanded="false"
          >
            ${icon('menu')}
          </button>
          <div class="context-controls" aria-label="Current resource context">
            <button type="button" data-context-switch="project">
              <span>Project</span>
              <b data-context-value="project">${contextState.project}</b>
              ${icon('chevron', 14)}
            </button>
            <i aria-hidden="true"></i>
            <button type="button" data-context-switch="environment">
              <span>Environment</span>
              <b data-context-value="environment">${contextState.environment}</b>
              ${icon('chevron', 14)}
            </button>
            <i aria-hidden="true"></i>
            <button type="button" data-context-switch="region">
              <span>Region</span>
              <b data-context-value="region">${contextState.region}</b>
              ${icon('chevron', 14)}
            </button>
          </div>
          <div class="context-actions">
            <button class="context-search" type="button" data-notify="Command search ready">
              <span>Search Aster</span>
              <kbd>⌘ K</kbd>
            </button>
            <button
              class="icon-button"
              type="button"
              data-notify="No new notifications"
              aria-label="Notifications"
            >
              <span class="notification-glyph">◌</span>
            </button>
            <button
              class="avatar-button"
              type="button"
              data-notify="Account menu opened"
              aria-label="Open account menu"
            >
              QD
            </button>
          </div>
        </header>
        <main id="main-content" tabindex="-1"></main>
      </div>
      <div
        class="toast"
        role="status"
        aria-live="polite"
        aria-atomic="true"
        data-toast
        hidden
      >
      </div>
      ${renderKeyDialog()}
    </div>
  `;
}

function renderKeyDialog(): string {
  return /* HTML */ `
    <dialog class="as-dialog key-dialog" id="api-key-dialog" aria-labelledby="key-dialog-title">
      <header class="as-dialog__header">
        <div>
          <span class="eyebrow">Project credential</span>
          <h2 id="key-dialog-title">Create API key</h2>
        </div>
        <form method="dialog">
          <button class="icon-button" type="submit" value="cancel" aria-label="Close dialog">
            ${icon('close')}
          </button>
        </form>
      </header>
      <div class="as-dialog__body" data-key-form-view>
        <p class="dialog-lead">
          The key belongs to
          <strong data-key-project>${contextState.project}</strong>
          . Set a limit so a leaked credential cannot spend the full project budget.
        </p>
        <form data-key-form novalidate>
          <label class="field">
            <span>Name</span>
            <input name="name" autocomplete="off" placeholder="e.g. production-support" required />
          </label>
          <label class="field">
            <span>Environment</span>
            <select name="environment">
              <option>Production</option>
              <option>Staging</option>
              <option>Development</option>
            </select>
          </label>
          <label class="field">
            <span>Monthly limit</span>
            <div class="input-prefix">
              <span>$</span>
              <input name="limit" type="number" value="250" min="1" required />
            </div>
            <small>Requests stop when this key reaches its limit.</small>
          </label>
          <p class="field-error" data-key-error role="alert" hidden></p>
          <footer class="dialog-actions">
            <button class="as-button" type="button" data-dialog-cancel>Cancel</button>
            <button class="as-button" data-variant="primary" type="submit">Create key</button>
          </footer>
        </form>
      </div>
      <div class="as-dialog__body key-result" data-key-result-view hidden>
        <div class="success-orbit">${icon('key', 24)}</div>
        <h3>Copy this key now</h3>
        <p>For security, Aster will not show the full value again.</p>
        <div class="secret-value">
          <code data-generated-key>ak_live_ast_7Q2M9VKC84</code>
          <button
            class="icon-button"
            type="button"
            data-copy-generated
            aria-label="Copy generated key"
          >
            ${icon('copy')}
          </button>
        </div>
        <button class="as-button" data-variant="primary" type="button" data-key-done>
          I've saved the key
        </button>
      </div>
    </dialog>
  `;
}

function pageHeader(eyebrow: string, title: string, description: string, actions = ''): string {
  return /* HTML */ `
    <header class="page-heading">
      <div>
        <span class="eyebrow">${eyebrow}</span>
        <h1>${title}</h1>
        <p>${description}</p>
      </div>
      ${
        actions === ''
          ? ''
          : /* HTML */ `
            <div class="page-actions">${actions}</div>
          `
      }
    </header>
  `;
}

function trendSvg(id: string, values: readonly number[], danger = false): string {
  const max = Math.max(...values);
  const min = Math.min(...values);
  const span = Math.max(max - min, 1);
  const points = values
    .map(
      (value, index) =>
        `${(index / Math.max(values.length - 1, 1)) * 100},${42 - ((value - min) / span) * 34}`,
    )
    .join(' ');
  return /* HTML */ `
    <svg
      class="trend-chart ${danger ? 'is-danger' : ''}"
      viewBox="0 0 100 48"
      preserveAspectRatio="none"
      role="img"
      aria-labelledby="${id}-title"
    >
      <title id="${id}-title">Trend over the last 13 periods</title>
      <path class="chart-grid" d="M0 42H100M0 24H100M0 7H100" />
      <polyline points="${points}" />
    </svg>
  `;
}

function renderOverview(): string {
  return /* HTML */ `
    <div class="page page-overview" data-page="overview">
      ${pageHeader(
        'Support Copilot · Production',
        'Good afternoon, Qiu.',
        'Your model API is healthy. Finish the canary review before traffic increases tonight.',
        routeLink('playground', `${icon('spark')} Open studio`, 'as-button', 'primary'),
      )}
      <section class="activation-band" aria-labelledby="activation-title">
        <header>
          <div>
            <span class="live-label">
              <i></i>
              Activation path
            </span>
            <h2 id="activation-title">From model to production in four connected steps</h2>
          </div>
          <span>3 of 4 complete</span>
        </header>
        <ol>
          <li data-state="done">
            <span>${icon('check')}</span>
            <div>
              <b>Choose a model</b>
              <small>Aurora 3.2 Reason</small>
            </div>
          </li>
          <li data-state="done">
            <span>${icon('check')}</span>
            <div>
              <b>Run a prompt</b>
              <small>42 successful runs</small>
            </div>
          </li>
          <li data-state="done">
            <span>${icon('check')}</span>
            <div>
              <b>Create endpoint</b>
              <small>support-copilot-prod</small>
            </div>
          </li>
          <li data-state="current">
            <span>4</span>
            <div>
              <b>Review canary</b>
              <small>10% on rev-19</small>
            </div>
            ${routeLink('endpoint', 'Review', 'text-link', 'quiet')}
          </li>
        </ol>
      </section>
      <div class="overview-grid">
        <section class="pulse-panel" aria-labelledby="pulse-title">
          <header>
            <div>
              <span class="eyebrow">Production pulse</span>
              <h2 id="pulse-title">184,200 requests</h2>
              <p>
                <b>+12.8%</b>
                over the previous 24 hours
              </p>
            </div>
            <div class="time-switch" aria-label="Chart range">
              <button class="is-active" type="button" data-chart-range>24h</button>
              <button type="button" data-chart-range>7d</button>
              <button type="button" data-chart-range>30d</button>
            </div>
          </header>
          ${trendSvg('request-trend', [31, 39, 35, 48, 44, 58, 55, 66, 61, 73, 70, 82, 88])}
          <footer>
            <dl>
              <div>
                <dt>P95 latency</dt>
                <dd>1.84 s</dd>
              </div>
              <div>
                <dt>Error rate</dt>
                <dd>0.27%</dd>
              </div>
              <div>
                <dt>Tokens / sec</dt>
                <dd>138</dd>
              </div>
            </dl>
            ${routeLink('requests', 'Inspect requests →', 'text-link', 'quiet')}
          </footer>
        </section>
        <aside class="overview-side">
          <section class="endpoint-snapshot">
            <header>
              <span class="status" data-tone="success">
                <i></i>
                Healthy
              </span>
              <span class="mono">ep_7c3f19</span>
            </header>
            <h2>${endpoint.name}</h2>
            <p>${endpoint.model} · ${endpoint.region}</p>
            <div class="replica-map" aria-label="3 active replicas">
              <span></span>
              <span></span>
              <span></span>
              <i></i>
              <i></i>
            </div>
            <dl>
              <div>
                <dt>Stable</dt>
                <dd>rev-18 · 90%</dd>
              </div>
              <div>
                <dt>Canary</dt>
                <dd>rev-19 · 10%</dd>
              </div>
            </dl>
            ${routeLink('endpoint', 'Open endpoint', 'as-button', 'secondary')}
          </section>
          <section class="budget-note">
            <span>${icon('info')}</span>
            <div>
              <b>74% of monthly budget used</b>
              <p>Forecast ${formatMoney(usage.forecast, 0)} of ${formatMoney(usage.budget, 0)}.</p>
            </div>
            ${routeLink('usage', 'Review', 'text-link', 'quiet')}
          </section>
        </aside>
      </div>
      <section class="activity-stream">
        <header>
          <div>
            <span class="eyebrow">Recent activity</span>
            <h2>What changed in this project</h2>
          </div>
          ${routeLink('requests', 'View request stream', 'text-link', 'quiet')}
        </header>
        <ol>
          <li>
            <span class="activity-icon">${icon('run')}</span>
            <div>
              <b>Evaluation completed</b>
              <p>Support policy regression · v18 reached 91.8 quality.</p>
            </div>
            <time>18 min ago</time>
          </li>
          <li>
            <span class="activity-icon">${icon('endpoint')}</span>
            <div>
              <b>Canary revision received traffic</b>
              <p>rev-19 is serving 10% in cn-shanghai.</p>
            </div>
            <time>42 min ago</time>
          </li>
          <li>
            <span class="activity-icon">${icon('key')}</span>
            <div>
              <b>API key used from a new client</b>
              <p>prod-support · edge gateway 10.42.18.7</p>
            </div>
            <time>2 h ago</time>
          </li>
        </ol>
      </section>
    </div>
  `;
}

function renderModels(): string {
  const providers = [...new Set(models.map((model) => model.provider))];
  return /* HTML */ `
    <div class="page page-models" data-page="models">
      ${pageHeader(
        'Discover',
        'Choose by task, not by hype.',
        'Compare the capabilities, speed, access path and regional availability that change production behavior.',
        routeLink('playground', `${icon('studio')} Open playground`, 'as-button', 'primary'),
      )}
      <section class="catalog-toolbar" aria-label="Model filters">
        <label class="catalog-search">
          ${icon('search')}
          <span class="sr-only">Search models</span>
          <input
            data-model-search
            type="search"
            placeholder="Search models, providers or capabilities"
            autocomplete="off"
          />
        </label>
        <label>
          <span>Modality</span>
          <select data-model-modality>
            <option>All</option>
            <option>Text</option>
            <option>Vision</option>
            <option>Image</option>
            <option>Embedding</option>
            <option>Audio</option>
          </select>
        </label>
        <label>
          <span>Provider</span>
          <select data-model-provider>
            <option>All</option>
            ${providers
              .map(
                (provider) => /* HTML */ `
                  <option>${provider}</option>
                `,
              )
              .join('')}
          </select>
        </label>
        <label>
          <span>Access</span>
          <select data-model-access>
            <option>All</option>
            <option>Serverless</option>
            <option>Dedicated</option>
            <option>Both</option>
          </select>
        </label>
      </section>
      <div class="catalog-summary">
        <p>
          <b data-model-count>${models.length}</b>
          production-ready models
        </p>
        <button type="button" class="as-button" data-variant="quiet" data-clear-models>
          ${icon('filter')} Clear filters
        </button>
      </div>
      <section class="model-list" aria-label="Model catalog">
        ${models
          .map(
            (model) => /* HTML */ `
              <article class="model-row" data-model-item="${model.id}">
                <div
                  class="model-glyph"
                  data-modality="${model.modalities[0]?.toLowerCase() ?? 'text'}"
                >
                  ${
                    model.modalities.includes('Image')
                      ? icon('image', 20)
                      : model.modalities.includes('Embedding')
                        ? '∿'
                        : model.modalities.includes('Audio')
                          ? '⌁'
                          : 'Aa'
                  }
                </div>
                <div class="model-copy">
                  <div>
                    <h2>${model.name}</h2>
                    ${
                      model.featured
                        ? '<span class="status" data-tone="info">Recommended</span>'
                        : ''
                    }
                  </div>
                  <p>${model.summary}</p>
                  <footer>
                    <span>${model.provider}</span>
                    ${model.capabilities
                      .map(
                        (capability) => /* HTML */ `
                          <i>${capability}</i>
                        `,
                      )
                      .join('')}
                  </footer>
                </div>
                <dl class="model-stats">
                  <div>
                    <dt>Context</dt>
                    <dd>${model.context === 0 ? 'Streaming' : formatTokens(model.context)}</dd>
                  </div>
                  <div>
                    <dt>Latency P50</dt>
                    <dd>${model.latencyP50} ms</dd>
                  </div>
                  <div>
                    <dt>Input / 1M</dt>
                    <dd>${formatMoney(model.inputPrice)}</dd>
                  </div>
                  <div>
                    <dt>Access</dt>
                    <dd>${model.access}</dd>
                  </div>
                </dl>
                ${routeLink(
                  'model-detail',
                  `Open ${icon('arrow')}`,
                  'as-button model-open',
                  'secondary',
                )}
              </article>
            `,
          )
          .join('')}
        <div class="catalog-empty" data-model-empty hidden>
          ${icon('search', 28)}
          <h2>No models match this view</h2>
          <p>Clear one filter or search by a broader capability such as “vision”.</p>
          <button class="as-button" data-variant="primary" type="button" data-clear-models>
            Clear all filters
          </button>
        </div>
      </section>
    </div>
  `;
}

function renderModelDetail(): string {
  const model = models[0];
  if (!model) return '';
  return /* HTML */ `
    <div class="page page-model-detail" data-page="model-detail">
      <a class="back-link" data-route href="${viewHref('models')}">← All models</a>
      <section class="model-hero">
        <div class="model-orbit">
          <span>A3</span>
          <i></i>
          <i></i>
          <i></i>
        </div>
        <div class="model-identity">
          <span class="eyebrow">${model.provider} · ${model.id}</span>
          <h1>${model.name}</h1>
          <p>${model.summary} Built for traceable tool use and multilingual support workflows.</p>
          <div class="capability-line">
            ${model.modalities
              .map(
                (value) => /* HTML */ `
                  <span>${value}</span>
                `,
              )
              .join('')}${model.capabilities
              .map(
                (value) => /* HTML */ `
                  <span>${value}</span>
                `,
              )
              .join('')}
          </div>
        </div>
        <div class="model-hero-actions">
          ${routeLink(
            'playground',
            `${icon('run')} Try in studio`,
            'as-button',
            'primary',
          )}${routeLink('deploy', 'Deploy dedicated', 'as-button', 'secondary')}
        </div>
      </section>
      <section class="spec-ribbon" aria-label="Model specifications">
        <dl>
          <div>
            <dt>Context window</dt>
            <dd>${formatTokens(model.context)}</dd>
            <small>tokens</small>
          </div>
          <div>
            <dt>Serverless price</dt>
            <dd>${formatMoney(model.inputPrice)}</dd>
            <small>input / 1M</small>
          </div>
          <div>
            <dt>Median latency</dt>
            <dd>${model.latencyP50} ms</dd>
            <small>cn-shanghai</small>
          </div>
          <div>
            <dt>Availability</dt>
            <dd>${model.availability}%</dd>
            <small>last 30 days</small>
          </div>
        </dl>
      </section>
      <div class="detail-grid">
        <section class="provider-section">
          <header>
            <div>
              <span class="eyebrow">Route intelligence</span>
              <h2>Choose the provider path</h2>
              <p>
                Every route serves the same model interface, but privacy, latency and price differ.
              </p>
            </div>
            <button class="as-button" type="button" data-notify="Route comparison pinned">
              Pin comparison
            </button>
          </header>
          <div
            class="as-table-region"
            role="region"
            aria-label="Provider route comparison"
            tabindex="0"
          >
            <table class="as-table provider-table">
              <thead>
                <tr>
                  <th scope="col">Provider route</th>
                  <th scope="col">Privacy</th>
                  <th scope="col">P50</th>
                  <th scope="col">Throughput</th>
                  <th scope="col">Input / output</th>
                  <th scope="col">Available</th>
                </tr>
              </thead>
              <tbody>
                ${providerRoutes
                  .map(
                    (route, index) => /* HTML */ `
                      <tr>
                        <th scope="row">
                          <span class="route-radio ${index === 0 ? 'is-selected' : ''}"></span>
                          <div>
                            <b>${route.provider}</b>
                            <small>${route.region}</small>
                          </div>
                        </th>
                        <td>${route.privacy}</td>
                        <td>${route.latencyP50} ms</td>
                        <td>${route.throughput} t/s</td>
                        <td>
                          ${formatMoney(route.inputPrice)} / ${formatMoney(route.outputPrice)}
                        </td>
                        <td>
                          <span class="status" data-tone="success">
                            <i></i>
                            ${route.availability}%
                          </span>
                        </td>
                      </tr>
                    `,
                  )
                  .join('')}
              </tbody>
            </table>
          </div>
        </section>
        <aside class="benchmark-panel">
          <header>
            <span class="eyebrow">Support benchmark</span>
            <h2>Strong where your project needs it</h2>
          </header>
          <ul>
            <li>
              <div>
                <span>Policy accuracy</span>
                <b>91.8</b>
              </div>
              <i style="--score:91.8%"></i>
            </li>
            <li>
              <div>
                <span>Grounded answers</span>
                <b>94.2</b>
              </div>
              <i style="--score:94.2%"></i>
            </li>
            <li>
              <div>
                <span>Tool selection</span>
                <b>88.6</b>
              </div>
              <i style="--score:88.6%"></i>
            </li>
            <li>
              <div>
                <span>Safety</span>
                <b>99.4</b>
              </div>
              <i style="--score:99.4%"></i>
            </li>
          </ul>
          <p>
            Measured on
            <b>support-policy-golden-v6</b>
            , 240 samples.
          </p>
          ${routeLink('evaluations', 'Open evaluation →', 'text-link', 'quiet')}
        </aside>
      </div>
      <section class="model-notes">
        <article>
          <span class="eyebrow">Best for</span>
          <h2>Reasoning across policy and tool output</h2>
          <p>
            Use Aurora when a support flow must retrieve a policy, explain a decision and return a
            stable JSON envelope.
          </p>
        </article>
        <article>
          <span class="eyebrow">Deployment note</span>
          <h2>Start shared, reserve when steady</h2>
          <p>
            The serverless route covers experiments. Dedicated L40S capacity becomes economical near
            110 sustained tokens per second.
          </p>
        </article>
      </section>
    </div>
  `;
}

type StudioMode = 'chat' | 'image' | 'embedding';

const studioModes: Record<
  StudioMode,
  { label: string; kicker: string; prompt: string; run: string; model: string }
> = {
  chat: {
    label: 'Chat',
    kicker: 'Reason over support policy',
    prompt:
      'A customer received an order on August 12. Explain whether they can return it today, ' +
      'cite the policy date, and output the next action.',
    run: 'Run prompt',
    model: 'Aurora 3.2 Reason',
  },
  image: {
    label: 'Image',
    kicker: 'Generate a campaign asset',
    prompt:
      'Create a clean editorial product image for a midnight-blue travel speaker, soft studio shadows, no text, 4:3.',
    run: 'Generate image',
    model: 'Canvas 2 Image',
  },
  embedding: {
    label: 'Embedding',
    kicker: 'Inspect semantic vectors',
    prompt:
      'Enterprise customers can request invoices in batches that match their settlement statements.',
    run: 'Create embedding',
    model: 'Vector 3 Embed',
  },
};

function renderStudioControls(mode: StudioMode): string {
  if (mode === 'chat') {
    return /* HTML */ `
      <label>
        <span>Temperature</span>
        <input type="range" min="0" max="100" value="20" />
        <b>0.2</b>
      </label>
      <label>
        <span>Max output</span>
        <select>
          <option>2,048 tokens</option>
          <option>4,096 tokens</option>
        </select>
      </label>
    `;
  }

  if (mode === 'image') {
    return /* HTML */ `
      <label>
        <span>Aspect ratio</span>
        <select>
          <option>4:3</option>
          <option>1:1</option>
          <option>16:9</option>
        </select>
      </label>
      <label>
        <span>Quality</span>
        <select>
          <option>High</option>
          <option>Standard</option>
        </select>
      </label>
    `;
  }

  return /* HTML */ `
    <label>
      <span>Dimensions</span>
      <select>
        <option>1,024</option>
        <option>768</option>
        <option>512</option>
      </select>
    </label>
    <label>
      <span>Normalize</span>
      <select>
        <option>Enabled</option>
        <option>Disabled</option>
      </select>
    </label>
  `;
}

function studioInput(mode: StudioMode): string {
  const config = studioModes[mode];
  const controls = renderStudioControls(mode);
  return /* HTML */ `
    <div class="composer-context">
      <span>${config.kicker}</span>
      <button type="button" data-notify="System instruction editor opened">
        ${
          mode === 'chat'
            ? 'System instruction'
            : mode === 'image'
              ? 'Reference image'
              : 'Batch input'
        }
        +
      </button>
    </div>
    <div class="as-composer studio-composer" data-mode="${mode}">
      <label for="studio-prompt">Prompt</label>
      <textarea id="studio-prompt">${config.prompt}</textarea>
      <footer>
        <div>
          <button
            class="tool-button"
            type="button"
            data-notify="File picker opened"
            aria-label="Attach context"
          >
            ＋
          </button>
          <button class="tool-button" type="button" data-notify="Tool selector opened">
            ${mode === 'chat' ? 'Tools' : mode === 'image' ? 'Seed · auto' : 'Input · text'}
          </button>
        </div>
        <button class="run-button" type="button" data-playground-run>
          ${icon('run')}
          <span>${config.run}</span>
        </button>
      </footer>
    </div>
    <div class="run-settings">
      <div class="model-setting">
        <span>Model</span>
        <button type="button" data-notify="Model picker opened">
          <i></i>
          ${config.model}${icon('chevron', 13)}
        </button>
      </div>
      ${controls}
      <button class="settings-more" type="button" data-notify="Advanced run settings opened">
        More settings
      </button>
    </div>
  `;
}

function studioResult(mode: StudioMode, state: 'idle' | 'loading' | 'complete'): string {
  if (state === 'loading')
    return /* HTML */ `
      <div class="stage-loading">
        <span></span>
        <p>
          ${
            mode === 'image'
              ? 'Composing image'
              : mode === 'embedding'
                ? 'Projecting vector'
                : 'Reasoning over 6 policy passages'
          }
        </p>
      </div>
    `;
  if (state === 'idle')
    return /* HTML */ `
      <div class="stage-idle">
        <div class="idle-spark">
          ${mode === 'image' ? icon('image', 30) : mode === 'embedding' ? '∿' : icon('spark', 30)}
        </div>
        <h2>
          ${
            mode === 'chat'
              ? 'Your answer will appear here'
              : mode === 'image'
                ? 'Your generated asset will appear here'
                : 'Vector dimensions will appear here'
          }
        </h2>
        <p>Run the prompt to see output, code and request events together.</p>
      </div>
    `;
  if (mode === 'image')
    return /* HTML */ `
      <div
        class="generated-asset"
        role="img"
        aria-label="Generated editorial image of a midnight-blue travel speaker"
      >
        <div class="asset-object">
          <i></i>
          <i></i>
          <span></span>
        </div>
        <div class="asset-shadow"></div>
        <span class="asset-tag">1536 × 1152 · seed 24819</span>
      </div>
    `;
  if (mode === 'embedding')
    return /* HTML */ `
      <div class="vector-result">
        <header>
          <span>VECTOR · 1,024 DIMENSIONS</span>
          <b>norm 1.000</b>
        </header>
        <div class="vector-field">
          ${Array.from(
            { length: 64 },
            (_, index) => /* HTML */ `
              <i style="--v:${22 + ((index * 37) % 76)}%"></i>
            `,
          ).join('')}
        </div>
        <dl>
          <div>
            <dt>Nearest concept</dt>
            <dd>batch invoice policy</dd>
          </div>
          <div>
            <dt>Cosine similarity</dt>
            <dd>0.9238</dd>
          </div>
          <div>
            <dt>Input tokens</dt>
            <dd>13</dd>
          </div>
        </dl>
      </div>
    `;
  return /* HTML */ `
    <article class="chat-result">
      <header>
        <span class="result-avatar">A</span>
        <div>
          <b>Aster response</b>
          <small>Aurora 3.2 Reason · Northstar direct</small>
        </div>
      </header>
      <div class="answer-copy">
        <p>
          The return window starts on the date the customer
          <strong>confirmed receipt</strong>
          , not the order date. For an August 12 receipt, the standard 7-day window ended on August
          19.
        </p>
        <p>
          <strong>Next action:</strong>
          route this case to manual review because today's request falls outside the automatic
          return window.
        </p>
        <aside>
          <span>[1]</span>
          <p>
            Returns policy v6 · §2.1
            <br />
            <b>“7 days from confirmed receipt”</b>
          </p>
        </aside>
      </div>
      <footer>
        <span>1,842 input · 486 output</span>
        <span>${formatMoney(0.0091, 4)}</span>
        <span>1.83 s</span>
      </footer>
    </article>
  `;
}

function renderPlayground(): string {
  return /* HTML */ `
    <div class="page studio-page" data-page="playground" data-studio-mode="chat">
      <header class="studio-heading">
        <div>
          <span class="eyebrow">Build · Untitled session</span>
          <h1>Multimodal studio</h1>
        </div>
        <div>
          <button class="as-button" type="button" data-notify="New session ready">
            New session
          </button>
          <button class="as-button" type="button" data-notify="Session saved">Save</button>
          <button class="as-button" data-variant="primary" type="button" data-create-key>
            ${icon('key')} Create API key
          </button>
        </div>
      </header>
      <div class="mode-switch" aria-label="Playground mode">
        ${(Object.entries(studioModes) as [StudioMode, (typeof studioModes)[StudioMode]][])
          .map(
            ([id, config], index) => /* HTML */ `
              <button type="button" data-studio-mode-button="${id}" aria-pressed="${index === 0}">
                <span>${id === 'chat' ? 'Aa' : id === 'image' ? icon('image') : '∿'}</span>
                <div>
                  <b>${config.label}</b>
                  <small>
                    ${
                      id === 'chat'
                        ? 'Messages & tools'
                        : id === 'image'
                          ? 'Generate & edit'
                          : 'Vectors & similarity'
                    }
                  </small>
                </div>
              </button>
            `,
          )
          .join('')}
      </div>
      <section class="studio-workspace" aria-label="Prompt workspace">
        <div class="studio-input" data-studio-input>${studioInput('chat')}</div>
        <div class="result-stage" data-live-stage aria-live="polite" aria-busy="false">
          <header>
            <div>
              <span class="live-label">
                <i></i>
                Live result
              </span>
              <small data-stage-model>Aurora 3.2 Reason</small>
            </div>
            <button class="icon-button" type="button" data-copy="stage" aria-label="Copy result">
              ${icon('copy')}
            </button>
          </header>
          <div class="stage-body" data-stage-body>${studioResult('chat', 'idle')}</div>
        </div>
      </section>
      <section class="studio-inspector">
        <div class="as-tabs inspector-tabs" role="tablist" aria-label="Run inspection" data-tabs>
          <button
            type="button"
            role="tab"
            id="inspect-output-tab"
            aria-selected="true"
            aria-controls="inspect-output-panel"
            tabindex="0"
          >
            Output
          </button>
          <button
            type="button"
            role="tab"
            id="inspect-code-tab"
            aria-selected="false"
            aria-controls="inspect-code-panel"
            tabindex="-1"
          >
            Code
          </button>
          <button
            type="button"
            role="tab"
            id="inspect-events-tab"
            aria-selected="false"
            aria-controls="inspect-events-panel"
            tabindex="-1"
          >
            Events
            <span>4</span>
          </button>
        </div>
        <div class="inspector-panels">
          <section role="tabpanel" id="inspect-output-panel" aria-labelledby="inspect-output-tab">
            <div class="output-summary">
              <span>Run summary</span>
              <p>Waiting for the first run. Token use, latency and route appear here.</p>
            </div>
          </section>
          <section
            role="tabpanel"
            id="inspect-code-panel"
            aria-labelledby="inspect-code-tab"
            hidden
          >
            <div class="code-head">
              <span>TypeScript · Responses API</span>
              <button type="button" data-copy="code">${icon('copy')} Copy</button>
            </div>
            <pre data-studio-code><code>const response = await aster.responses.create({
  model: 'aurora-3-2-reason',
  input: prompt,
  tools: ['search_knowledge']
});</code></pre>
          </section>
          <section
            role="tabpanel"
            id="inspect-events-panel"
            aria-labelledby="inspect-events-tab"
            hidden
          >
            <ol class="event-list" data-studio-events>
              <li>
                <time>0 ms</time>
                <i></i>
                <div>
                  <b>Waiting for a run</b>
                  <p>Gateway, tool and model events will stream here.</p>
                </div>
              </li>
            </ol>
          </section>
        </div>
      </section>
    </div>
  `;
}

const deploySteps = ['Model', 'Capacity', 'Security', 'Review'] as const;

function deployStepMarkup(step: number, draft: DeployDraft): string {
  if (step === 0) {
    return /* HTML */ `
      <fieldset class="choice-fieldset">
        <legend>
          <span class="eyebrow">01 · Model</span>
          <strong>What should this endpoint serve?</strong>
          <small>Dedicated endpoints pin a model version and reserve capacity.</small>
        </legend>
        <div class="choice-list">
          ${models
            .filter((model) => model.access !== 'Serverless')
            .map(
              (model) => /* HTML */ `
                <label class="choice-row">
                  <input
                    type="radio"
                    name="model"
                    value="${model.name}"
                    ${draft.model === model.name ? 'checked' : ''}
                  />
                  <span class="choice-marker"></span>
                  <div>
                    <b>${model.name}</b>
                    <p>${model.provider} · ${model.modalities.join(' + ')}</p>
                  </div>
                  <dl>
                    <div>
                      <dt>P50</dt>
                      <dd>${model.latencyP50} ms</dd>
                    </div>
                    <div>
                      <dt>Throughput</dt>
                      <dd>${model.throughput} t/s</dd>
                    </div>
                  </dl>
                </label>
              `,
            )
            .join('')}
        </div>
      </fieldset>
    `;
  }
  if (step === 1) {
    return /* HTML */ `
      <div class="wizard-form">
        <header>
          <span class="eyebrow">02 · Capacity</span>
          <h2>Place and size the runtime</h2>
          <p>
            Traffic stays in the selected region. You can change maximum replicas after deployment.
          </p>
        </header>
        <fieldset>
          <legend>Region</legend>
          <div class="option-grid">
            ${['cn-shanghai', 'ap-southeast-1', 'us-west-2']
              .map(
                (region) => /* HTML */ `
                  <label>
                    <input
                      type="radio"
                      name="region"
                      value="${region}"
                      ${draft.region === region ? 'checked' : ''}
                    />
                    <span>
                      <b>${region}</b>
                      <small>
                        ${
                          region === 'cn-shanghai'
                            ? 'Best latency · 42 ms'
                            : region === 'ap-southeast-1'
                              ? 'Available · 84 ms'
                              : 'Available · 166 ms'
                        }
                      </small>
                    </span>
                  </label>
                `,
              )
              .join('')}
          </div>
        </fieldset>
        <fieldset>
          <legend>Hardware profile</legend>
          <div class="option-grid hardware-grid">
            ${[
              ['L4 · 24 GB', '$1.32 / h', 'Balanced'],
              ['L40S · 48 GB', '$2.18 / h', 'Recommended'],
              ['A100 · 80 GB', '$4.26 / h', 'Maximum context'],
            ]
              .map(
                ([hardware, price, label]) => /* HTML */ `
                  <label>
                    <input
                      type="radio"
                      name="hardware"
                      value="${hardware}"
                      ${draft.hardware === hardware ? 'checked' : ''}
                    />
                    <span>
                      <em>${label}</em>
                      <b>${hardware}</b>
                      <small>${price} per replica</small>
                    </span>
                  </label>
                `,
              )
              .join('')}
          </div>
        </fieldset>
        <label class="replica-control">
          <span>
            <b>Minimum replicas</b>
            <small>Always ready to receive traffic.</small>
          </span>
          <input type="range" name="replicas" min="1" max="8" value="${draft.replicas}" />
          <output data-replica-output>${draft.replicas}</output>
        </label>
      </div>
    `;
  }
  if (step === 2) {
    return /* HTML */ `
      <div class="wizard-form">
        <header>
          <span class="eyebrow">03 · Security</span>
          <h2>Name and protect the endpoint</h2>
          <p>Authentication is required for every production endpoint.</p>
        </header>
        <label class="field">
          <span>Endpoint name</span>
          <input
            name="endpointName"
            value="${draft.endpointName}"
            pattern="[a-z0-9-]+"
            aria-describedby="endpoint-name-hint"
          />
          <small id="endpoint-name-hint">Lowercase letters, numbers and hyphens.</small>
        </label>
        <fieldset>
          <legend>Client authentication</legend>
          <div class="security-options">
            <label>
              <input
                type="radio"
                name="authentication"
                value="project-key"
                ${draft.authentication === 'project-key' ? 'checked' : ''}
              />
              <span>
                ${icon('key')}
                <div>
                  <b>Project API keys</b>
                  <small>Use scoped credentials with budgets and expiry.</small>
                </div>
              </span>
            </label>
            <label>
              <input
                type="radio"
                name="authentication"
                value="private-network"
                ${draft.authentication === 'private-network' ? 'checked' : ''}
              />
              <span>
                ${icon('endpoint')}
                <div>
                  <b>Private network</b>
                  <small>Only traffic from the Meridian service network.</small>
                </div>
              </span>
            </label>
          </div>
        </fieldset>
        <label class="inline-check">
          <input type="checkbox" checked />
          <span>
            <b>Zero-retention request logs</b>
            <small>Store metadata and trace timing, but redact request bodies after 7 days.</small>
          </span>
        </label>
      </div>
    `;
  }
  return /* HTML */ `
    <div class="review-sheet">
      <header>
        <span class="eyebrow">04 · Review</span>
        <h2>Ready to reserve capacity</h2>
        <p>
          Aster will create one endpoint and a first deployment revision. Provisioning usually takes
          4–6 minutes.
        </p>
      </header>
      <dl>
        <div>
          <dt>Endpoint</dt>
          <dd>${draft.endpointName}</dd>
        </div>
        <div>
          <dt>Model</dt>
          <dd>${draft.model}</dd>
        </div>
        <div>
          <dt>Region</dt>
          <dd>${draft.region}</dd>
        </div>
        <div>
          <dt>Hardware</dt>
          <dd>${draft.hardware} × ${draft.replicas}</dd>
        </div>
        <div>
          <dt>Authentication</dt>
          <dd>
            ${draft.authentication === 'project-key' ? 'Project API keys' : 'Private network'}
          </dd>
        </div>
        <div>
          <dt>Retention</dt>
          <dd>
            Metadata · 30 days
            <br />
            Bodies · redacted after 7 days
          </dd>
        </div>
      </dl>
      <aside>
        ${icon('info')}
        <p>
          Creating this endpoint adds approximately
          <strong>${formatMoney(estimateHourlyCost(draft))} per hour</strong>
          while all minimum replicas are active.
        </p>
      </aside>
    </div>
  `;
}

function renderDeploy(): string {
  return /* HTML */ `
    <div class="page deploy-page" data-page="deploy">
      <header class="deploy-heading">
        <div>
          <a class="back-link" data-route href="${viewHref('model-detail')}">← Aurora 3.2 Reason</a>
          <h1>Create dedicated endpoint</h1>
          <p>Reserve production capacity through four reviewable decisions.</p>
        </div>
        <span class="secure-label">${icon('key')} Project-scoped</span>
      </header>
      <ol class="wizard-progress" aria-label="Deployment progress">
        ${deploySteps
          .map(
            (label, index) => /* HTML */ `
              <li data-deploy-step-indicator="${index}" ${index === 0 ? 'aria-current="step"' : ''}>
                <span>${index + 1}</span>
                <b>${label}</b>
              </li>
            `,
          )
          .join('')}
      </ol>
      <div class="deploy-layout">
        <section class="wizard-panel" data-deploy-panel>
          ${deployStepMarkup(0, emptyDeployDraft())}
        </section>
        <aside class="deploy-summary" aria-label="Configuration summary">
          <header>
            <span class="eyebrow">Live estimate</span>
            <span>USD</span>
          </header>
          <div class="cost-estimate">
            <strong data-deploy-cost>$2.64</strong>
            <span>/ hour</span>
            <p data-deploy-monthly>About $1,927 / month</p>
          </div>
          <dl>
            <div>
              <dt>Model</dt>
              <dd data-summary-model>Choose a model</dd>
            </div>
            <div>
              <dt>Region</dt>
              <dd data-summary-region>Not selected</dd>
            </div>
            <div>
              <dt>Capacity</dt>
              <dd data-summary-hardware>2 replicas</dd>
            </div>
            <div>
              <dt>Auth</dt>
              <dd data-summary-auth>Not selected</dd>
            </div>
          </dl>
          <p class="estimate-note">
            Estimate excludes serverless calls, storage and outbound data transfer.
          </p>
        </aside>
      </div>
      <footer class="wizard-actions">
        <p class="field-error" data-deploy-error role="alert" hidden></p>
        <div>
          <button class="as-button" type="button" data-deploy-back disabled>Back</button>
          <button class="as-button" data-variant="primary" type="button" data-deploy-next>
            Continue ${icon('arrow')}
          </button>
        </div>
      </footer>
    </div>
  `;
}

const endpointTabs = [
  'Overview',
  'Playground',
  'Analytics',
  'Usage',
  'Logs',
  'Versions',
  'Settings',
] as const;

function endpointTabButton(label: string, index: number): string {
  const slug = label.toLowerCase();
  return /* HTML */ `
    <button
      type="button"
      role="tab"
      id="endpoint-${slug}-tab"
      aria-selected="${index === 0}"
      aria-controls="endpoint-${slug}-panel"
      tabindex="${index === 0 ? '0' : '-1'}"
    >
      ${label}
    </button>
  `;
}

function renderEndpoint(): string {
  const barHeights = [31, 38, 34, 46, 52, 47, 62, 58, 70, 66, 81, 75, 88, 84, 92, 79, 86, 94];
  return /* HTML */ `
    <div class="page endpoint-page" data-page="endpoint">
      <header class="endpoint-heading">
        <div>
          <span class="eyebrow">Dedicated endpoint · ${endpoint.id}</span>
          <div>
            <h1>${endpoint.name}</h1>
            <span class="status" data-tone="success">
              <i></i>
              ${endpoint.status}
            </span>
          </div>
          <p>${endpoint.model} · ${endpoint.version} · ${endpoint.region}</p>
        </div>
        <div>
          <button class="as-button" type="button" data-copy="${endpoint.url}">
            ${icon('copy')} Copy URL
          </button>
          ${routeLink('deploy', `${icon('plus')} New endpoint`, 'as-button', 'primary')}
        </div>
      </header>
      <div class="endpoint-url">
        <span>POST</span>
        <code>${endpoint.url}</code>
        <small>OpenAI-compatible</small>
      </div>
      <div class="as-tabs endpoint-tabs" role="tablist" aria-label="Endpoint sections" data-tabs>
        ${endpointTabs.map(endpointTabButton).join('')}
      </div>
      <div class="endpoint-panels">
        <section
          role="tabpanel"
          id="endpoint-overview-panel"
          aria-labelledby="endpoint-overview-tab"
        >
          <div class="endpoint-overview-grid">
            <section class="traffic-panel">
              <header>
                <div>
                  <span class="eyebrow">Traffic allocation</span>
                  <h2>Two live revisions</h2>
                </div>
                <button class="as-button" type="button" data-notify="Traffic editor opened">
                  Edit split
                </button>
              </header>
              <div class="traffic-rail">
                <i style="--share:90%"></i>
                <i style="--share:10%"></i>
              </div>
              <ul>
                <li>
                  <span data-revision="stable"></span>
                  <div>
                    <b>rev-18</b>
                    <small>Stable · Aug 12</small>
                  </div>
                  <strong>90%</strong>
                </li>
                <li>
                  <span data-revision="canary"></span>
                  <div>
                    <b>rev-19</b>
                    <small>Canary · Today</small>
                  </div>
                  <strong>10%</strong>
                </li>
              </ul>
            </section>
            <section class="runtime-card">
              <span class="eyebrow">Runtime</span>
              <h2>3 of 3 replicas ready</h2>
              <div class="runtime-nodes">
                <i></i>
                <i></i>
                <i></i>
                <span></span>
              </div>
              <dl>
                <div>
                  <dt>Hardware</dt>
                  <dd>L40S · 48 GB</dd>
                </div>
                <div>
                  <dt>Autoscale</dt>
                  <dd>2–8 replicas</dd>
                </div>
                <div>
                  <dt>Timeout</dt>
                  <dd>120 seconds</dd>
                </div>
                <div>
                  <dt>Auth</dt>
                  <dd>Project keys</dd>
                </div>
              </dl>
            </section>
          </div>
          <section class="runtime-events">
            <header>
              <div>
                <span class="eyebrow">Deployment events</span>
                <h2>Runtime, separate from request logs</h2>
              </div>
              <span class="status" data-tone="success">
                <i></i>
                Live
              </span>
            </header>
            <ol>
              <li>
                <time>12:31:06</time>
                <span class="event-dot"></span>
                <div>
                  <b>Canary traffic changed to 10%</b>
                  <p>Revision rev-19 · by qiu@meridian.example</p>
                </div>
              </li>
              <li>
                <time>12:28:44</time>
                <span class="event-dot"></span>
                <div>
                  <b>Replica 3 became ready</b>
                  <p>Health probe passed in cn-shanghai-b</p>
                </div>
              </li>
              <li>
                <time>12:26:12</time>
                <span class="event-dot"></span>
                <div>
                  <b>Revision rev-19 deployed</b>
                  <p>Image verified · model weights mounted</p>
                </div>
              </li>
            </ol>
          </section>
        </section>
        <section
          role="tabpanel"
          id="endpoint-playground-panel"
          aria-labelledby="endpoint-playground-tab"
          hidden
        >
          <div class="endpoint-try">
            <div>
              <span class="eyebrow">Endpoint prompt</span>
              <h2>Test the production route</h2>
              <p>
                Runs appear in request logs with the
                <code>console-endpoint-test</code>
                key.
              </p>
              <label for="endpoint-prompt">Message</label>
              <textarea id="endpoint-prompt">
Summarize the return policy and cite the relevant section.</textarea
              >
              <button class="as-button" data-variant="primary" type="button" data-endpoint-run>
                ${icon('run')} Run on endpoint
              </button>
            </div>
            <aside data-endpoint-result>
              <span>${icon('spark')}</span>
              <p>Run the message to inspect the production response.</p>
            </aside>
          </div>
        </section>
        <section
          role="tabpanel"
          id="endpoint-analytics-panel"
          aria-labelledby="endpoint-analytics-tab"
          hidden
        >
          <section class="metric-strip">
            <dl>
              <div>
                <dt>Requests · 24h</dt>
                <dd>${formatTokens(endpoint.metrics.requests24h)}</dd>
                <small>↑ 12.8%</small>
              </div>
              <div>
                <dt>P95 latency</dt>
                <dd>${endpoint.metrics.p95} ms</dd>
                <small>↓ 4.2%</small>
              </div>
              <div>
                <dt>Error rate</dt>
                <dd>${endpoint.metrics.errorRate}%</dd>
                <small>Within SLO</small>
              </div>
              <div>
                <dt>Tokens / sec</dt>
                <dd>${endpoint.metrics.tokensPerSecond}</dd>
                <small>Per replica</small>
              </div>
            </dl>
          </section>
          <section class="analytics-main">
            <header>
              <div>
                <span class="eyebrow">Throughput</span>
                <h2>Requests per five minutes</h2>
              </div>
              <span>Stable + canary</span>
            </header>
            <div class="bar-field" aria-label="Request throughput chart">
              ${barHeights
                .map(
                  (height) => /* HTML */ `
                    <i style="--bar:${height}%"></i>
                  `,
                )
                .join('')}
            </div>
            <footer>
              <span>00:00</span>
              <span>06:00</span>
              <span>12:00</span>
              <span>18:00</span>
              <span>Now</span>
            </footer>
          </section>
        </section>
        <section
          role="tabpanel"
          id="endpoint-usage-panel"
          aria-labelledby="endpoint-usage-tab"
          hidden
        >
          <div class="usage-endpoint">
            <section>
              <span class="eyebrow">August compute</span>
              <h2>${formatMoney(626.42)}</h2>
              <p>34.0% of project spend</p>
              ${trendSvg('endpoint-cost', [28, 36, 32, 42, 51, 46, 58, 61, 55, 66, 72, 76, 81])}
            </section>
            <dl>
              <div>
                <dt>Reserved compute</dt>
                <dd>${formatMoney(542.8)}</dd>
              </div>
              <div>
                <dt>Scale-out time</dt>
                <dd>18.4 hours</dd>
              </div>
              <div>
                <dt>Input tokens</dt>
                <dd>72.8M</dd>
              </div>
              <div>
                <dt>Output tokens</dt>
                <dd>18.4M</dd>
              </div>
            </dl>
          </div>
        </section>
        <section
          role="tabpanel"
          id="endpoint-logs-panel"
          aria-labelledby="endpoint-logs-tab"
          hidden
        >
          <div class="panel-toolbar">
            <label>
              ${icon('search')}
              <input
                type="search"
                data-endpoint-log-search
                aria-label="Search endpoint logs"
                placeholder="Search runtime events"
              />
            </label>
            <button class="as-button" type="button" data-notify="Log export queued">Export</button>
          </div>
          <div class="log-console" role="log">
            <div data-endpoint-log-row>
              <time>12:31:06.928</time>
              <span data-level="info">INFO</span>
              <code>traffic.update</code>
              <p>rev-19 allocation set to 10%</p>
            </div>
            <div data-endpoint-log-row>
              <time>12:28:44.102</time>
              <span data-level="info">INFO</span>
              <code>replica.ready</code>
              <p>replica-3 passed readiness probe</p>
            </div>
            <div data-endpoint-log-row>
              <time>12:27:21.515</time>
              <span data-level="warn">WARN</span>
              <code>cache.warm</code>
              <p>prefix cache below target hit rate</p>
            </div>
            <p data-endpoint-log-empty hidden>No runtime events match this search.</p>
          </div>
        </section>
        <section
          role="tabpanel"
          id="endpoint-versions-panel"
          aria-labelledby="endpoint-versions-tab"
          hidden
        >
          <div class="version-stack">
            <article>
              <span data-revision="canary"></span>
              <div>
                <header>
                  <h2>rev-19</h2>
                  <span class="status" data-tone="info">Canary · 10%</span>
                </header>
                <p>Aurora 3.2 Reason · 2026-08-12 · prompt cache enabled</p>
                <small>Deployed 42 minutes ago by qiu@meridian.example</small>
              </div>
              <button class="as-button" type="button" data-notify="Canary promotion review opened">
                Promote
              </button>
            </article>
            <article>
              <span data-revision="stable"></span>
              <div>
                <header>
                  <h2>rev-18</h2>
                  <span class="status" data-tone="success">Stable · 90%</span>
                </header>
                <p>Aurora 3.2 Reason · 2026-08-12 · standard cache</p>
                <small>Deployed 13 days ago by platform@meridian.example</small>
              </div>
              <button class="as-button" type="button" data-notify="Rollback options opened">
                •••
              </button>
            </article>
          </div>
        </section>
        <section
          role="tabpanel"
          id="endpoint-settings-panel"
          aria-labelledby="endpoint-settings-tab"
          hidden
        >
          <form class="settings-sheet" data-endpoint-settings>
            <header>
              <span class="eyebrow">Endpoint settings</span>
              <h2>Limits and retention</h2>
            </header>
            <label class="field">
              <span>Display name</span>
              <input value="Support Copilot production" />
            </label>
            <div class="settings-two">
              <label class="field">
                <span>Request timeout</span>
                <select>
                  <option>120 seconds</option>
                  <option>60 seconds</option>
                </select>
              </label>
              <label class="field">
                <span>Maximum replicas</span>
                <input type="number" value="8" min="2" max="24" />
              </label>
            </div>
            <label class="inline-check">
              <input type="checkbox" checked />
              <span>
                <b>Store trace metadata</b>
                <small>Keep route, token and latency details for 30 days.</small>
              </span>
            </label>
            <footer>
              <button class="as-button" data-variant="primary" type="submit">Save changes</button>
            </footer>
          </form>
        </section>
      </div>
    </div>
  `;
}

function renderEvaluations(): string {
  const selected = evaluation.badCases[0];
  if (!selected) return '';
  return /* HTML */ `
    <div class="page evaluation-page" data-page="evaluations">
      ${pageHeader(
        'Evaluate · Completed Aug 24',
        evaluation.name,
        `${evaluation.dataset} · ${evaluation.samples} samples · automatic and human review`,
        /* HTML */ `
          <button class="as-button" type="button" data-notify="Evaluation export queued">
            Export report
          </button>
          <button
            class="as-button"
            data-variant="primary"
            type="button"
            data-notify="New evaluation draft created"
          >
            New evaluation
          </button>
        `,
      )}
      <section class="evaluation-scoreboard" aria-label="Evaluation summary">
        <div class="quality-score">
          <span>Winning quality</span>
          <strong>${evaluation.candidates[0]?.quality ?? 0}</strong>
          <small>Aurora 3.2 Reason</small>
          <i style="--score:${evaluation.candidates[0]?.quality ?? 0}%"></i>
        </div>
        <div class="score-metrics">
          <dl>
            <div>
              <dt>Grounded</dt>
              <dd>94.2</dd>
              <small>+5.1 vs Atlas</small>
            </div>
            <div>
              <dt>Safety</dt>
              <dd>99.4</dd>
              <small>-0.3 vs Atlas</small>
            </div>
            <div>
              <dt>Bad cases</dt>
              <dd>18</dd>
              <small>7.5% of set</small>
            </div>
            <div>
              <dt>Human agreement</dt>
              <dd>93%</dd>
              <small>42 reviewed</small>
            </div>
          </dl>
        </div>
      </section>
      <section class="candidate-compare">
        <header>
          <div>
            <span class="eyebrow">Candidate comparison</span>
            <h2>Quality gains cost 800 ms at P95</h2>
          </div>
          <span class="legend">
            <i></i>
            Best score
          </span>
        </header>
        <div
          class="as-table-region"
          role="region"
          aria-label="Evaluation candidate comparison"
          tabindex="0"
        >
          <table class="as-table">
            <thead>
              <tr>
                <th scope="col">Candidate</th>
                <th scope="col">Quality</th>
                <th scope="col">Grounded</th>
                <th scope="col">Safety</th>
                <th scope="col">P95</th>
                <th scope="col">Decision</th>
              </tr>
            </thead>
            <tbody>
              ${evaluation.candidates
                .map(
                  (candidate, index) => /* HTML */ `
                    <tr>
                      <th scope="row">
                        <span class="candidate-dot" data-index="${index}"></span>
                        ${candidate.model}
                      </th>
                      <td><b>${candidate.quality}</b></td>
                      <td>${candidate.grounded}</td>
                      <td>${candidate.safety}</td>
                      <td>${candidate.p95} ms</td>
                      <td>
                        ${
                          index === 0
                            ? '<span class="status" data-tone="success">Promote</span>'
                            : index === 1
                              ? '<span class="status" data-tone="neutral">Fallback</span>'
                              : '<span class="status" data-tone="warning">Investigate</span>'
                        }
                      </td>
                    </tr>
                  `,
                )
                .join('')}
            </tbody>
          </table>
        </div>
      </section>
      <section class="bad-case-section">
        <header>
          <div>
            <span class="eyebrow">Sample review</span>
            <h2>Investigate the failures, not just the average</h2>
          </div>
          <label>
            <span>Category</span>
            <select data-eval-filter>
              <option value="all">All bad cases</option>
              <option value="Policy date">Policy date</option>
              <option value="Citation">Citation</option>
              <option value="Escalation">Escalation</option>
              <option value="Safety">Safety</option>
            </select>
          </label>
        </header>
        <div class="bad-case-layout">
          <div
            class="as-table-region sample-table"
            role="region"
            aria-label="Bad case samples"
            tabindex="0"
          >
            <table class="as-table">
              <thead>
                <tr>
                  <th scope="col">Sample</th>
                  <th scope="col">Category</th>
                  <th scope="col">Input</th>
                  <th scope="col">Score</th>
                </tr>
              </thead>
              <tbody>
                ${evaluation.badCases
                  .map(
                    (sample, index) => /* HTML */ `
                      <tr
                        data-eval-row
                        data-category="${sample.category}"
                        class="${index === 0 ? 'is-selected' : ''}"
                      >
                        <th scope="row">
                          <button type="button" data-eval-sample="${sample.id}">
                            ${sample.id}
                          </button>
                        </th>
                        <td>
                          <span
                            class="status"
                            data-tone="${sample.score === 0 ? 'danger' : 'warning'}"
                          >
                            ${sample.category}
                          </span>
                        </td>
                        <td>${sample.input}</td>
                        <td>${sample.score.toFixed(1)}</td>
                      </tr>
                    `,
                  )
                  .join('')}
              </tbody>
            </table>
            <div class="sample-empty" data-eval-empty hidden>
              <h3>No bad cases in this category</h3>
              <p>Choose All bad cases to continue the review.</p>
            </div>
          </div>
          <aside class="sample-review" data-sample-review>${renderSampleReview(selected)}</aside>
        </div>
      </section>
    </div>
  `;
}

function renderSampleReview(sample: (typeof evaluation.badCases)[number]): string {
  return /* HTML */ `
    <header>
      <div>
        <span class="eyebrow">${sample.id}</span>
        <h2>${sample.category}</h2>
      </div>
      <span class="score-badge" data-score="${sample.score === 0 ? 'fail' : 'partial'}">
        ${sample.score.toFixed(1)}
      </span>
    </header>
    <section>
      <h3>Input</h3>
      <p>${sample.input}</p>
    </section>
    <section>
      <h3>Expected</h3>
      <p>${sample.expected}</p>
    </section>
    <section class="actual-output">
      <h3>Candidate output</h3>
      <p>${sample.output}</p>
    </section>
    <footer>
      <a
        class="as-button"
        data-variant="secondary"
        data-route
        href="${viewHref('requests')}&trace=${encodeURIComponent(sample.traceId)}"
      >
        Open trace ${sample.traceId} →
      </a>
      <button class="as-button" type="button" data-notify="Sample assigned to you">
        Assign to me
      </button>
    </footer>
  `;
}

function requestTone(status: RequestRecord['status']): string {
  return status === 200 ? 'success' : status === 429 ? 'warning' : 'danger';
}

function renderRequestDetail(request: RequestRecord): string {
  return /* HTML */ `
    <header>
      <div>
        <span class="status" data-tone="${requestTone(request.status)}">
          <i></i>
          ${request.status}
        </span>
        <code>${request.id}</code>
      </div>
      <button
        class="icon-button"
        type="button"
        data-copy="${request.id}"
        aria-label="Copy request ID"
      >
        ${icon('copy')}
      </button>
    </header>
    <dl class="request-facts">
      <div>
        <dt>Total latency</dt>
        <dd>${request.latency} ms</dd>
      </div>
      <div>
        <dt>First token</dt>
        <dd>${request.ttft === 0 ? '—' : `${request.ttft} ms`}</dd>
      </div>
      <div>
        <dt>Tokens</dt>
        <dd>
          ${request.inputTokens.toLocaleString()} in · ${request.outputTokens.toLocaleString()} out
        </dd>
      </div>
      <div>
        <dt>Cost</dt>
        <dd>${formatMoney(request.cost, 4)}</dd>
      </div>
      <div>
        <dt>Route</dt>
        <dd>${request.route}</dd>
      </div>
      <div>
        <dt>API key</dt>
        <dd>${request.key}</dd>
      </div>
    </dl>
    <section class="trace-view">
      <header>
        <div>
          <span class="eyebrow">Trace · ${request.traceId}</span>
          <h2>Where the ${request.latency} ms went</h2>
        </div>
        <span>0 → ${request.latency} ms</span>
      </header>
      <ol>
        ${traceSpans
          .map(
            (span, index) => /* HTML */ `
              <li data-kind="${span.kind}">
                <div class="span-tree">
                  <span style="--depth:${index === 0 ? 0 : 1}"></span>
                  <i></i>
                  <b>${span.label}</b>
                </div>
                <div class="span-track">
                  <i
                    style="--start:${Math.min(
                      (span.start / Math.max(request.latency, 1)) * 100,
                      92,
                    )}%;--duration:${Math.max(
                      Math.min((span.duration / Math.max(request.latency, 1)) * 100, 100),
                      4,
                    )}%"
                  >
                  </i>
                </div>
                <time>${span.duration} ms</time>
                <p>${span.detail}</p>
              </li>
            `,
          )
          .join('')}
      </ol>
    </section>
    <section class="request-payload">
      <header>
        <h3>Request input</h3>
        <span>Redacts sensitive data</span>
      </header>
      <pre><code>{
  "model": "${request.model}",
  "input": "Summarize the applicable return policy…",
  "tools": ["search_knowledge"]
}</code></pre>
    </section>
    <footer>
      <a
        class="as-button"
        data-variant="primary"
        data-route
        data-replay="${request.id}"
        href="${viewHref('playground')}&replay=${request.id}"
      >
        ${icon('run')} Replay in studio
      </a>
      <button class="as-button" type="button" data-notify="Request marked for evaluation">
        Add to evaluation
      </button>
    </footer>
  `;
}

function renderRequests(): string {
  const traceId = new URLSearchParams(window.location.search).get('trace');
  const selected =
    requestRecords.find((request) => request.traceId === traceId) ?? requestRecords[0];
  if (!selected) return '';
  return /* HTML */ `
    <div class="page requests-page" data-page="requests">
      ${pageHeader(
        'Operate · Live request stream',
        'Requests and traces',
        'Follow one production call across the gateway, policy layer, tools and model route.',
        /* HTML */ `
          <button class="as-button" type="button" data-notify="Live tail paused">
            <span class="health-dot"></span>
            Live tail
          </button>
          <button class="as-button" type="button" data-notify="Request export queued">
            Export
          </button>
        `,
      )}
      <section class="request-toolbar" aria-label="Request filters">
        <label class="request-search">
          ${icon('search')}
          <input
            type="search"
            data-request-search
            placeholder="Request ID, trace, key or route"
            aria-label="Search requests"
          />
        </label>
        <label>
          <span>Status</span>
          <select data-request-status>
            <option value="all">All statuses</option>
            <option value="200">200</option>
            <option value="429">429</option>
            <option value="500">500</option>
          </select>
        </label>
        <label>
          <span>Model</span>
          <select data-request-model>
            <option value="all">All models</option>
            ${[...new Set(requestRecords.map((request) => request.model))]
              .map(
                (model) => /* HTML */ `
                  <option>${model}</option>
                `,
              )
              .join('')}
          </select>
        </label>
        <button class="as-button" type="button" data-clear-requests>Clear</button>
      </section>
      <div class="request-layout">
        <section class="request-list" aria-label="Request results">
          <header>
            <span>
              <b data-request-count>${requestRecords.length}</b>
              requests · last 15 minutes
            </span>
            <span>Newest first</span>
          </header>
          <div class="as-table-region" role="region" aria-label="Request result table" tabindex="0">
            <table class="as-table">
              <thead>
                <tr>
                  <th scope="col">Time / request</th>
                  <th scope="col">Status</th>
                  <th scope="col">Model</th>
                  <th scope="col">Latency</th>
                  <th scope="col">Tokens</th>
                  <th scope="col">Cost</th>
                </tr>
              </thead>
              <tbody>
                ${requestRecords
                  .map(
                    (request) => /* HTML */ `
                      <tr
                        data-request-row
                        data-status="${request.status}"
                        data-model="${request.model}"
                        class="${request.id === selected.id ? 'is-selected' : ''}"
                      >
                        <th scope="row">
                          <time>${request.time}</time>
                          <button type="button" data-request-item="${request.id}">
                            ${request.id}
                          </button>
                        </th>
                        <td>
                          <span class="status" data-tone="${requestTone(request.status)}">
                            <i></i>
                            ${request.status}
                          </span>
                        </td>
                        <td>
                          ${request.model}
                          <small>${request.route}</small>
                        </td>
                        <td>${request.latency} ms</td>
                        <td>${request.inputTokens + request.outputTokens}</td>
                        <td>${formatMoney(request.cost, 4)}</td>
                      </tr>
                    `,
                  )
                  .join('')}
              </tbody>
            </table>
            <div class="request-empty" data-request-empty hidden>
              <h3>No requests match these filters</h3>
              <p>Clear a filter or search a broader identifier.</p>
              <button class="as-button" data-variant="primary" type="button" data-clear-requests>
                Clear filters
              </button>
            </div>
          </div>
        </section>
        <aside class="request-detail" data-request-detail>${renderRequestDetail(selected)}</aside>
      </div>
    </div>
  `;
}

function renderApiKeyRows(): string {
  const existingRows = usage.apiKeys
    .map(
      (key) => /* HTML */ `
        <tr>
          <th scope="row">${key.name}</th>
          <td><code>${key.prefix}</code></td>
          <td>${key.lastUsed}</td>
          <td>${formatMoney(key.limit, 0)}</td>
          <td>
            <span class="status" data-tone="success">
              <i></i>
              Active
            </span>
          </td>
          <td>
            <button
              class="icon-button"
              type="button"
              data-notify="Key actions opened"
              aria-label="Open actions for ${key.name}"
            >
              •••
            </button>
          </td>
        </tr>
      `,
    )
    .join('');
  const createdRows = createdKeys
    .filter((key) => key.project === contextState.project)
    .map(
      (key) => /* HTML */ `
        <tr data-created-key="${escapeHtml(key.name)}">
          <th scope="row">${escapeHtml(key.name)}</th>
          <td><code>${key.prefix}</code></td>
          <td>${key.lastUsed}</td>
          <td>${formatMoney(key.limit, 0)}</td>
          <td>
            <span class="status" data-tone="success">
              <i></i>
              Active
            </span>
          </td>
          <td>
            <button
              class="icon-button"
              type="button"
              data-notify="Key actions opened"
              aria-label="Open actions for ${escapeHtml(key.name)}"
            >
              •••
            </button>
          </td>
        </tr>
      `,
    )
    .join('');
  return `${createdRows}${existingRows}`;
}

function renderUsage(): string {
  return /* HTML */ `
    <div class="page usage-page" data-page="usage">
      ${pageHeader(
        'Operate · August 2026',
        'Usage, budgets and keys',
        'Attribute spend to the model, route and credential that created it.',
        /* HTML */ `
          <button class="as-button" type="button" data-notify="Usage CSV export queued">
            Export CSV
          </button>
          <button class="as-button" data-variant="primary" type="button" data-create-key>
            ${icon('key')} Create API key
          </button>
        `,
      )}
      <section class="spend-hero">
        <div class="spend-copy">
          <span>Current spend</span>
          <h2>${formatMoney(usage.currentSpend)}</h2>
          <p>
            <b>${formatMoney(usage.budget - usage.currentSpend)}</b>
            remains from the ${formatMoney(usage.budget, 0)} monthly budget.
          </p>
          <div class="budget-meter">
            <i style="--spent:${(usage.currentSpend / usage.budget) * 100}%"></i>
            <span style="--forecast:${(usage.forecast / usage.budget) * 100}%"></span>
          </div>
          <footer>
            <span>74% used</span>
            <span>Forecast ${formatMoney(usage.forecast, 0)}</span>
            <span>Budget ${formatMoney(usage.budget, 0)}</span>
          </footer>
        </div>
        <div class="spend-chart">
          <header>
            <span class="eyebrow">Cumulative cost</span>
            <b>Aug 12–25</b>
          </header>
          ${trendSvg('spend-trend', usage.byDay)}
          <footer>
            <span>Aug 12</span>
            <span>Aug 18</span>
            <span>Today</span>
          </footer>
        </div>
        <aside>
          <span class="status" data-tone="warning">Forecast risk</span>
          <h3>94.7% projected</h3>
          <p>Aurora dedicated compute accounts for most of the increase.</p>
          <button class="as-button" type="button" data-notify="Budget alert editor opened">
            Edit alert
          </button>
        </aside>
      </section>
      <div class="as-tabs attribution-tabs" role="tablist" aria-label="Usage attribution" data-tabs>
        <button
          type="button"
          role="tab"
          id="usage-model-tab"
          aria-selected="true"
          aria-controls="usage-model-panel"
          tabindex="0"
        >
          By model
        </button>
        <button
          type="button"
          role="tab"
          id="usage-project-tab"
          aria-selected="false"
          aria-controls="usage-project-panel"
          tabindex="-1"
        >
          By project
        </button>
        <button
          type="button"
          role="tab"
          id="usage-key-tab"
          aria-selected="false"
          aria-controls="usage-key-panel"
          tabindex="-1"
        >
          By API key
        </button>
        <button
          type="button"
          role="tab"
          id="usage-provider-tab"
          aria-selected="false"
          aria-controls="usage-provider-panel"
          tabindex="-1"
        >
          By provider
        </button>
      </div>
      <section
        class="attribution-panel"
        role="tabpanel"
        id="usage-model-panel"
        aria-labelledby="usage-model-tab"
      >
        <header>
          <span>Model</span>
          <span>Share</span>
          <span>Tokens</span>
          <span>Cost</span>
        </header>
        ${usage.byModel
          .map(
            (row, index) => /* HTML */ `
              <div>
                <span>
                  <i data-index="${index}"></i>
                  <b>${row.name}</b>
                </span>
                <span>
                  <em style="--share:${row.share}%"></em>
                  ${row.share}%
                </span>
                <span>${formatTokens(row.tokens)}</span>
                <strong>${formatMoney(row.cost)}</strong>
              </div>
            `,
          )
          .join('')}
      </section>
      <section
        class="attribution-panel alternate-panel"
        role="tabpanel"
        id="usage-project-panel"
        aria-labelledby="usage-project-tab"
        hidden
      >
        <header>
          <span>Project</span>
          <span>Environment</span>
          <span>Share</span>
          <span>Cost</span>
        </header>
        <div>
          <span>
            <i data-index="0"></i>
            <b>Support Copilot</b>
          </span>
          <span>Production</span>
          <span>86.2%</span>
          <strong>${formatMoney(1588.16)}</strong>
        </div>
        <div>
          <span>
            <i data-index="1"></i>
            <b>Policy evaluation</b>
          </span>
          <span>Staging</span>
          <span>13.8%</span>
          <strong>${formatMoney(254.2)}</strong>
        </div>
      </section>
      <section
        class="attribution-panel alternate-panel"
        role="tabpanel"
        id="usage-key-panel"
        aria-labelledby="usage-key-tab"
        hidden
      >
        <header>
          <span>API key</span>
          <span>Last used</span>
          <span>Limit</span>
          <span>Cost</span>
        </header>
        ${usage.apiKeys
          .map(
            (key, index) => /* HTML */ `
              <div>
                <span>
                  <i data-index="${index}"></i>
                  <b>${key.name}</b>
                </span>
                <span>${key.lastUsed}</span>
                <span>${formatMoney(key.limit, 0)}</span>
                <strong>${formatMoney([1312.2, 402.18, 127.98][index] ?? 0)}</strong>
              </div>
            `,
          )
          .join('')}
      </section>
      <section
        class="attribution-panel alternate-panel"
        role="tabpanel"
        id="usage-provider-panel"
        aria-labelledby="usage-provider-tab"
        hidden
      >
        <header>
          <span>Provider</span>
          <span>Route</span>
          <span>Share</span>
          <span>Cost</span>
        </header>
        <div>
          <span>
            <i data-index="0"></i>
            <b>Northstar direct</b>
          </span>
          <span>Dedicated + shared</span>
          <span>68.1%</span>
          <strong>${formatMoney(1254.68)}</strong>
        </div>
        <div>
          <span>
            <i data-index="1"></i>
            <b>Meridian capacity</b>
          </span>
          <span>Shared</span>
          <span>23.8%</span>
          <strong>${formatMoney(438.82)}</strong>
        </div>
        <div>
          <span>
            <i data-index="2"></i>
            <b>Other providers</b>
          </span>
          <span>Shared</span>
          <span>8.1%</span>
          <strong>${formatMoney(148.86)}</strong>
        </div>
      </section>
      <section class="key-management">
        <header>
          <div>
            <span class="eyebrow">Project access</span>
            <h2>API keys</h2>
            <p>Keys inherit the current project and can have stricter spend limits.</p>
          </div>
          <button class="as-button" type="button" data-create-key>
            ${icon('plus')} Create key
          </button>
        </header>
        <div class="as-table-region" role="region" aria-label="API keys" tabindex="0">
          <table class="as-table">
            <thead>
              <tr>
                <th scope="col">Name</th>
                <th scope="col">Credential</th>
                <th scope="col">Last used</th>
                <th scope="col">Monthly limit</th>
                <th scope="col">Status</th>
                <th scope="col">Action</th>
              </tr>
            </thead>
            <tbody data-key-table>${renderApiKeyRows()}</tbody>
          </table>
        </div>
      </section>
    </div>
  `;
}

function getElement<T extends Element>(root: ParentNode, selector: string): T {
  const element = root.querySelector<T>(selector);
  if (!element) throw new Error(`Missing required element: ${selector}`);
  return element;
}

function listen<K extends keyof HTMLElementEventMap>(
  target: HTMLElement | Document,
  type: K,
  handler: (event: HTMLElementEventMap[K]) => void,
  signal?: AbortSignal,
): void;
function listen<K extends keyof WindowEventMap>(
  target: Window,
  type: K,
  handler: (event: WindowEventMap[K]) => void,
  signal?: AbortSignal,
): void;
function listen(
  target: EventTarget,
  type: string,
  handler: EventListener,
  signal?: AbortSignal,
): void {
  target.addEventListener(type, handler, signal ? { signal } : undefined);
}

function notify(message: string): void {
  const toast = getElement<HTMLElement>(app, '[data-toast]');
  window.clearTimeout(toastTimer);
  toast.textContent = message;
  toast.hidden = false;
  toast.dataset.visible = 'true';
  toastTimer = window.setTimeout(() => {
    toast.dataset.visible = 'false';
    window.setTimeout(() => {
      toast.hidden = true;
    }, 160);
  }, 2800);
}

async function copyText(value: string): Promise<void> {
  try {
    await navigator.clipboard.writeText(value);
    notify('Copied to clipboard');
  } catch {
    notify('Clipboard unavailable — select and copy the value');
  }
}

function setMobileNav(open: boolean): void {
  const shell = getElement<HTMLElement>(app, '.app-shell');
  const trigger = getElement<HTMLButtonElement>(app, '[data-mobile-nav-trigger]');
  shell.dataset.navOpen = String(open);
  trigger.setAttribute('aria-expanded', String(open));
  if (open) getElement<HTMLElement>(app, '.app-rail nav a').focus();
  else trigger.focus();
}

function initShell(): void {
  listen(app, 'click', (event) => {
    const target = event.target as Element;
    const route = target.closest<HTMLAnchorElement>('a[data-route]');
    if (
      route &&
      event instanceof MouseEvent &&
      event.button === 0 &&
      !event.metaKey &&
      !event.ctrlKey &&
      !event.shiftKey &&
      !event.altKey
    ) {
      const url = new URL(route.href, window.location.href);
      if (url.origin === window.location.origin && url.pathname === window.location.pathname) {
        event.preventDefault();
        history.pushState({}, '', `${url.search}${url.hash}`);
        renderPage(readPage(url.search), true);
        const shell = getElement<HTMLElement>(app, '.app-shell');
        shell.dataset.navOpen = 'false';
        getElement<HTMLButtonElement>(app, '[data-mobile-nav-trigger]').setAttribute(
          'aria-expanded',
          'false',
        );
      }
    }

    const navTrigger = target.closest<HTMLButtonElement>('[data-mobile-nav-trigger]');
    if (navTrigger) setMobileNav(true);
    if (target.closest('[data-mobile-nav-close]')) setMobileNav(false);

    const notification = target.closest<HTMLElement>('[data-notify]');
    if (notification?.dataset.notify) notify(notification.dataset.notify);

    const copy = target.closest<HTMLElement>('[data-copy]');
    if (copy) {
      const value =
        copy.dataset.copy === 'stage'
          ? getElement<HTMLElement>(app, '[data-stage-body]').innerText
          : copy.dataset.copy === 'code'
            ? (getElement<HTMLElement>(app, '[data-studio-code]').textContent ?? '')
            : (copy.dataset.copy ?? '');
      void copyText(value);
    }

    const replay = target.closest<HTMLElement>('[data-replay]');
    if (replay?.dataset.replay) sessionStorage.setItem('aster-replay', replay.dataset.replay);

    const contextSwitch = target.closest<HTMLButtonElement>('[data-context-switch]');
    if (contextSwitch) cycleContext(contextSwitch.dataset.contextSwitch ?? '');
  });

  listen(window, 'popstate', () => renderPage(readPage(window.location.search), true));
  listen(document, 'keydown', (event) => {
    if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 'k') {
      event.preventDefault();
      notify('Search models, requests or endpoints');
    }
    if (
      event.key === 'Escape' &&
      getElement<HTMLElement>(app, '.app-shell').dataset.navOpen === 'true'
    ) {
      setMobileNav(false);
    }
  });
  initKeyDialog();
}

function cycleContext(key: string): void {
  const values: Record<string, readonly string[]> = {
    project: ['Support Copilot', 'Policy Evaluation', 'Commerce Assistant'],
    environment: ['Production', 'Staging', 'Development'],
    region: ['cn-shanghai', 'ap-southeast-1', 'us-west-2'],
  };
  const list = values[key];
  if (!list || !(key in contextState)) return;
  const current = contextState[key as keyof typeof contextState];
  const next = list[(list.indexOf(current) + 1) % list.length] ?? list[0];
  if (!next) return;
  if (key === 'project') contextState.project = next;
  if (key === 'environment') contextState.environment = next;
  if (key === 'region') contextState.region = next;
  for (const value of app.querySelectorAll<HTMLElement>(`[data-context-value="${key}"]`))
    value.textContent = next;
  if (key === 'project') {
    const keyProject = app.querySelector<HTMLElement>('[data-key-project]');
    if (keyProject) keyProject.textContent = next;
  }
  notify(`${key[0]?.toUpperCase() ?? ''}${key.slice(1)} changed to ${next}`);
}

function initKeyDialog(): void {
  const dialog = getElement<HTMLDialogElement>(app, '#api-key-dialog');
  const form = getElement<HTMLFormElement>(dialog, '[data-key-form]');
  const formView = getElement<HTMLElement>(dialog, '[data-key-form-view]');
  const resultView = getElement<HTMLElement>(dialog, '[data-key-result-view]');
  const error = getElement<HTMLElement>(dialog, '[data-key-error]');
  let pendingKey: (typeof createdKeys)[number] | null = null;

  listen(app, 'click', (event) => {
    const target = event.target as Element;
    const trigger = target.closest<HTMLElement>('[data-create-key]');
    if (trigger) {
      lastDialogTrigger = trigger;
      form.reset();
      pendingKey = null;
      getElement<HTMLElement>(dialog, '[data-key-project]').textContent = contextState.project;
      formView.hidden = false;
      resultView.hidden = true;
      error.hidden = true;
      dialog.showModal();
      window.setTimeout(() => {
        const nameControl = form.elements.namedItem('name');
        if (nameControl instanceof HTMLElement) nameControl.focus();
      }, 0);
    }
    if (target.closest('[data-dialog-cancel]')) dialog.close('cancel');
    if (target.closest('[data-key-done]')) {
      const completedKey = pendingKey;
      dialog.close('created');
      if (completedKey) persistCreatedKey(completedKey);
      notify('API key created');
    }
    if (target.closest('[data-copy-generated]')) {
      void copyText(getElement<HTMLElement>(dialog, '[data-generated-key]').textContent ?? '');
    }
  });

  listen(form, 'submit', (event) => {
    event.preventDefault();
    const data = new FormData(form);
    const name = String(data.get('name') ?? '').trim();
    const limit = Number(data.get('limit'));
    if (name.length < 3 || !Number.isFinite(limit) || limit < 1) {
      error.textContent = 'Enter a name of at least 3 characters and a positive monthly limit.';
      error.hidden = false;
      return;
    }
    pendingKey = {
      name,
      prefix: 'ak_live_••••84KC',
      lastUsed: 'Just now',
      limit,
      project: contextState.project,
      environment: String(data.get('environment') ?? contextState.environment),
    };
    formView.hidden = true;
    resultView.hidden = false;
    getElement<HTMLButtonElement>(resultView, '[data-copy-generated]').focus();
  });

  listen(dialog, 'close', () => {
    lastDialogTrigger?.focus();
    lastDialogTrigger = null;
  });
}

function persistCreatedKey(key: (typeof createdKeys)[number]): void {
  if (
    createdKeys.some(
      (candidate) => candidate.project === key.project && candidate.name === key.name,
    )
  )
    return;
  createdKeys.unshift(key);
  const main = getElement<HTMLElement>(app, '#main-content');
  if (main.querySelector('[data-page="usage"]')) renderPage('usage');
}

function initTabs(root: HTMLElement, signal: AbortSignal): void {
  for (const tablist of root.querySelectorAll<HTMLElement>('[data-tabs][role="tablist"]')) {
    const tabs = [...tablist.querySelectorAll<HTMLButtonElement>('[role="tab"]')];
    const activate = (tab: HTMLButtonElement, focus: boolean): void => {
      for (const candidate of tabs) {
        const selected = candidate === tab;
        candidate.setAttribute('aria-selected', String(selected));
        candidate.tabIndex = selected ? 0 : -1;
        const panelId = candidate.getAttribute('aria-controls');
        const panel = panelId ? root.querySelector<HTMLElement>(`#${CSS.escape(panelId)}`) : null;
        if (panel) panel.hidden = !selected;
      }
      if (focus) tab.focus();
    };
    for (const tab of tabs) {
      listen(tab, 'click', () => activate(tab, false), signal);
      listen(
        tab,
        'keydown',
        (event) => {
          if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
          event.preventDefault();
          const direction = event.key === 'ArrowRight' ? 1 : -1;
          const index = tabs.indexOf(tab);
          const target = tabs[(index + direction + tabs.length) % tabs.length];
          if (target) activate(target, true);
        },
        signal,
      );
    }
  }
}

function initOverview(root: HTMLElement, signal: AbortSignal): void {
  for (const button of root.querySelectorAll<HTMLButtonElement>('[data-chart-range]')) {
    listen(
      button,
      'click',
      () => {
        for (const candidate of root.querySelectorAll('[data-chart-range]'))
          candidate.classList.toggle('is-active', candidate === button);
        notify(`Production pulse updated to ${button.textContent ?? 'selected range'}`);
      },
      signal,
    );
  }
}

function initModels(root: HTMLElement, signal: AbortSignal): void {
  const search = getElement<HTMLInputElement>(root, '[data-model-search]');
  const modality = getElement<HTMLSelectElement>(root, '[data-model-modality]');
  const provider = getElement<HTMLSelectElement>(root, '[data-model-provider]');
  const access = getElement<HTMLSelectElement>(root, '[data-model-access]');
  const items = [...root.querySelectorAll<HTMLElement>('[data-model-item]')];
  const empty = getElement<HTMLElement>(root, '[data-model-empty]');
  const count = getElement<HTMLElement>(root, '[data-model-count]');
  const apply = (): void => {
    const result = filterModels(models, {
      query: search.value,
      modality: modality.value as CatalogModality,
      provider: provider.value,
      access: access.value as CatalogAccess,
    });
    const visible = new Set(result.map((model) => model.id));
    for (const item of items) item.hidden = !visible.has(item.dataset.modelItem ?? '');
    empty.hidden = result.length !== 0;
    count.textContent = String(result.length);
  };
  const clear = (): void => {
    search.value = '';
    modality.value = 'All';
    provider.value = 'All';
    access.value = 'All';
    apply();
    search.focus();
  };
  listen(search, 'input', apply, signal);
  listen(modality, 'change', apply, signal);
  listen(provider, 'change', apply, signal);
  listen(access, 'change', apply, signal);
  listen(
    root,
    'click',
    (event) => {
      if ((event.target as Element).closest('[data-clear-models]')) clear();
    },
    signal,
  );
}

function initPlayground(root: HTMLElement, signal: AbortSignal): void {
  let mode: StudioMode = 'chat';
  let runTimer = 0;
  const input = getElement<HTMLElement>(root, '[data-studio-input]');
  const stage = getElement<HTMLElement>(root, '[data-live-stage]');
  const stageBody = getElement<HTMLElement>(root, '[data-stage-body]');
  const stageModel = getElement<HTMLElement>(root, '[data-stage-model]');
  const code = getElement<HTMLElement>(root, '[data-studio-code]');
  const events = getElement<HTMLElement>(root, '[data-studio-events]');
  const output = getElement<HTMLElement>(root, '#inspect-output-panel');

  const setMode = (next: StudioMode): void => {
    mode = next;
    root.dataset.studioMode = next;
    for (const button of root.querySelectorAll<HTMLButtonElement>('[data-studio-mode-button]'))
      button.setAttribute('aria-pressed', String(button.dataset.studioModeButton === next));
    input.innerHTML = studioInput(next);
    stageBody.innerHTML = studioResult(next, 'idle');
    stageModel.textContent = studioModes[next].model;
    const modelId =
      next === 'chat'
        ? 'aurora-3-2-reason'
        : next === 'image'
          ? 'canvas-2-image'
          : 'vector-3-embed';
    code.innerHTML = /* HTML */ `
      <code>
        const result = await
        aster.${
          next === 'chat'
            ? 'responses.create'
            : next === 'image'
              ? 'images.generate'
              : 'embeddings.create'
        }({
        model: '${modelId}', input: prompt });
      </code>
    `;
    events.innerHTML = /* HTML */ `
      <li>
        <time>0 ms</time>
        <i></i>
        <div>
          <b>Waiting for a run</b>
          <p>Gateway and model events will stream here.</p>
        </div>
      </li>
    `;
    output.innerHTML = /* HTML */ `
      <div class="output-summary">
        <span>Run summary</span>
        <p>Waiting for the first run. Usage and route details appear here.</p>
      </div>
    `;
  };

  const run = (button: HTMLButtonElement): void => {
    window.clearTimeout(runTimer);
    button.disabled = true;
    stage.setAttribute('aria-busy', 'true');
    stageBody.innerHTML = studioResult(mode, 'loading');
    events.innerHTML = /* HTML */ `
      <li>
        <time>0 ms</time>
        <i></i>
        <div>
          <b>Request accepted</b>
          <p>Project and key scope validated.</p>
        </div>
      </li>
      <li>
        <time>38 ms</time>
        <i></i>
        <div>
          <b>Model route selected</b>
          <p>Lowest-latency healthy provider in cn-shanghai.</p>
        </div>
      </li>
    `;
    runTimer = window.setTimeout(
      () => {
        stageBody.innerHTML = studioResult(mode, 'complete');
        stage.setAttribute('aria-busy', 'false');
        button.disabled = false;
        output.innerHTML = /* HTML */ `
          <div class="output-summary is-complete">
            <span>Run complete</span>
            <dl>
              <div>
                <dt>${mode === 'image' ? 'Images' : 'Tokens'}</dt>
                <dd>${mode === 'image' ? '1' : mode === 'embedding' ? '13' : '2,328'}</dd>
              </div>
              <div>
                <dt>Latency</dt>
                <dd>${mode === 'image' ? '2.41 s' : mode === 'embedding' ? '41 ms' : '1.83 s'}</dd>
              </div>
              <div>
                <dt>Cost</dt>
                <dd>
                  ${mode === 'image' ? '$0.0800' : mode === 'embedding' ? '$0.0001' : '$0.0091'}
                </dd>
              </div>
              <div>
                <dt>Route</dt>
                <dd>
                  ${
                    mode === 'chat'
                      ? 'Northstar direct'
                      : mode === 'image'
                        ? 'Luma shared'
                        : 'Harbor routed'
                  }
                </dd>
              </div>
            </dl>
          </div>
        `;
        events.innerHTML += /* HTML */ `
          <li>
            <time>${mode === 'embedding' ? '41' : mode === 'image' ? '2,410' : '1,834'} ms</time>
            <i></i>
            <div>
              <b>Run completed</b>
              <p>Usage attributed to console-studio key.</p>
            </div>
          </li>
        `;
        notify(`${studioModes[mode].label} result ready`);
      },
      mode === 'image' ? 900 : 650,
    );
  };

  listen(
    root,
    'click',
    (event) => {
      const target = event.target as Element;
      const modeButton = target.closest<HTMLButtonElement>('[data-studio-mode-button]');
      if (modeButton) setMode(modeButton.dataset.studioModeButton as StudioMode);
      const runButton = target.closest<HTMLButtonElement>('[data-playground-run]');
      if (runButton) run(runButton);
    },
    signal,
  );
  signal.addEventListener('abort', () => window.clearTimeout(runTimer), { once: true });

  const replay =
    new URLSearchParams(window.location.search).get('replay') ??
    sessionStorage.getItem('aster-replay');
  if (replay) {
    const textarea = root.querySelector<HTMLTextAreaElement>('#studio-prompt');
    if (textarea)
      textarea.value =
        'Replay the failed support-policy request. Compare the expected return window and explain the correction.';
    sessionStorage.removeItem('aster-replay');
    notify(`Replay loaded from ${replay}`);
  }
}

function initDeploy(root: HTMLElement, signal: AbortSignal): void {
  const draft = emptyDeployDraft();
  let step = 0;
  let deployTimer = 0;
  const panel = getElement<HTMLElement>(root, '[data-deploy-panel]');
  const next = getElement<HTMLButtonElement>(root, '[data-deploy-next]');
  const back = getElement<HTMLButtonElement>(root, '[data-deploy-back]');
  const error = getElement<HTMLElement>(root, '[data-deploy-error]');

  const syncDraft = (): void => {
    const model = panel.querySelector<HTMLInputElement>('input[name="model"]:checked');
    const region = panel.querySelector<HTMLInputElement>('input[name="region"]:checked');
    const hardware = panel.querySelector<HTMLInputElement>('input[name="hardware"]:checked');
    const auth = panel.querySelector<HTMLInputElement>('input[name="authentication"]:checked');
    const replicas = panel.querySelector<HTMLInputElement>('input[name="replicas"]');
    const name = panel.querySelector<HTMLInputElement>('input[name="endpointName"]');
    if (model) draft.model = model.value;
    if (region) draft.region = region.value;
    if (hardware) draft.hardware = hardware.value;
    if (auth) draft.authentication = auth.value as DeployDraft['authentication'];
    if (replicas) draft.replicas = Number(replicas.value);
    if (name) draft.endpointName = name.value;
    updateSummary();
  };

  const updateSummary = (): void => {
    const cost = estimateHourlyCost(draft);
    getElement(root, '[data-summary-model]').textContent = draft.model || 'Choose a model';
    getElement(root, '[data-summary-region]').textContent = draft.region || 'Not selected';
    getElement(root, '[data-summary-hardware]').textContent =
      `${draft.hardware || 'Hardware pending'} · ${draft.replicas} replicas`;
    getElement(root, '[data-summary-auth]').textContent =
      draft.authentication === ''
        ? 'Not selected'
        : draft.authentication === 'project-key'
          ? 'Project API keys'
          : 'Private network';
    getElement(root, '[data-deploy-cost]').textContent = formatMoney(cost);
    getElement(root, '[data-deploy-monthly]').textContent =
      `About ${formatMoney(cost * 24 * 30, 0)} / month`;
    const output = panel.querySelector<HTMLOutputElement>('[data-replica-output]');
    if (output) output.value = String(draft.replicas);
  };

  const renderStep = (): void => {
    panel.innerHTML = deployStepMarkup(step, draft);
    for (const indicator of root.querySelectorAll<HTMLElement>('[data-deploy-step-indicator]')) {
      const index = Number(indicator.dataset.deployStepIndicator);
      indicator.toggleAttribute('aria-current', index === step);
      indicator.dataset.state = index < step ? 'done' : index === step ? 'current' : 'future';
    }
    back.disabled = step === 0;
    next.innerHTML =
      step === deploySteps.length - 1
        ? `Create endpoint ${icon('arrow')}`
        : `Continue ${icon('arrow')}`;
    error.hidden = true;
    updateSummary();
  };

  listen(panel, 'input', syncDraft, signal);
  listen(panel, 'change', syncDraft, signal);
  listen(
    back,
    'click',
    () => {
      syncDraft();
      step = Math.max(0, step - 1);
      renderStep();
    },
    signal,
  );
  listen(
    next,
    'click',
    () => {
      syncDraft();
      const message = deployStepError(step, draft);
      if (message) {
        error.textContent = message;
        error.hidden = false;
        panel.querySelector<HTMLElement>('input:not([type="range"])')?.focus();
        return;
      }
      if (step < deploySteps.length - 1) {
        step += 1;
        renderStep();
        panel.scrollIntoView({
          behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth',
          block: 'start',
        });
        return;
      }
      next.disabled = true;
      next.textContent = 'Provisioning endpoint…';
      deployTimer = window.setTimeout(() => {
        history.pushState({}, '', viewHref('endpoint'));
        renderPage('endpoint', true);
        notify('Endpoint provisioning started');
      }, 800);
    },
    signal,
  );
  signal.addEventListener('abort', () => window.clearTimeout(deployTimer), { once: true });
  renderStep();
}

function initEndpoint(root: HTMLElement, signal: AbortSignal): void {
  let timer = 0;
  const run = root.querySelector<HTMLButtonElement>('[data-endpoint-run]');
  const result = root.querySelector<HTMLElement>('[data-endpoint-result]');
  if (run && result)
    listen(
      run,
      'click',
      () => {
        run.disabled = true;
        result.innerHTML = /* HTML */ `
          <div class="stage-loading">
            <span></span>
            <p>Calling production endpoint</p>
          </div>
        `;
        timer = window.setTimeout(() => {
          result.innerHTML = /* HTML */ `
            <article>
              <span class="result-avatar">A</span>
              <p>
                The standard return window is 7 days from confirmed receipt. See Returns policy
                v6, §2.1.
              </p>
              <small>1.64 s · 382 tokens · $0.0048</small>
            </article>
          `;
          run.disabled = false;
          notify('Production response received');
        }, 650);
      },
      signal,
    );

  const search = root.querySelector<HTMLInputElement>('[data-endpoint-log-search]');
  if (search)
    listen(
      search,
      'input',
      () => {
        const query = search.value.toLowerCase();
        let shown = 0;
        for (const row of root.querySelectorAll<HTMLElement>('[data-endpoint-log-row]')) {
          row.hidden = !row.innerText.toLowerCase().includes(query);
          if (!row.hidden) shown += 1;
        }
        const empty = getElement<HTMLElement>(root, '[data-endpoint-log-empty]');
        empty.hidden = shown !== 0;
      },
      signal,
    );

  const settings = root.querySelector<HTMLFormElement>('[data-endpoint-settings]');
  if (settings)
    listen(
      settings,
      'submit',
      (event) => {
        event.preventDefault();
        notify('Endpoint settings saved');
      },
      signal,
    );
  signal.addEventListener('abort', () => window.clearTimeout(timer), { once: true });
}

function initEvaluations(root: HTMLElement, signal: AbortSignal): void {
  const review = getElement<HTMLElement>(root, '[data-sample-review]');
  const filter = getElement<HTMLSelectElement>(root, '[data-eval-filter]');
  const rows = [...root.querySelectorAll<HTMLElement>('[data-eval-row]')];
  const empty = getElement<HTMLElement>(root, '[data-eval-empty]');
  listen(
    root,
    'click',
    (event) => {
      const button = (event.target as Element).closest<HTMLButtonElement>('[data-eval-sample]');
      if (!button) return;
      const sample = evaluation.badCases.find(
        (candidate) => candidate.id === button.dataset.evalSample,
      );
      if (!sample) return;
      for (const row of rows) row.classList.toggle('is-selected', row.contains(button));
      review.innerHTML = renderSampleReview(sample);
    },
    signal,
  );
  listen(
    filter,
    'change',
    () => {
      let shown = 0;
      for (const row of rows) {
        row.hidden = filter.value !== 'all' && row.dataset.category !== filter.value;
        if (!row.hidden) shown += 1;
      }
      empty.hidden = shown !== 0;
    },
    signal,
  );
}

function initRequests(root: HTMLElement, signal: AbortSignal): void {
  const search = getElement<HTMLInputElement>(root, '[data-request-search]');
  const status = getElement<HTMLSelectElement>(root, '[data-request-status]');
  const model = getElement<HTMLSelectElement>(root, '[data-request-model]');
  const rows = [...root.querySelectorAll<HTMLElement>('[data-request-row]')];
  const count = getElement<HTMLElement>(root, '[data-request-count]');
  const empty = getElement<HTMLElement>(root, '[data-request-empty]');
  const detail = getElement<HTMLElement>(root, '[data-request-detail]');
  const apply = (): void => {
    const query = search.value.toLowerCase();
    let shown = 0;
    for (const row of rows) {
      const visible =
        row.innerText.toLowerCase().includes(query) &&
        (status.value === 'all' || row.dataset.status === status.value) &&
        (model.value === 'all' || row.dataset.model === model.value);
      row.hidden = !visible;
      if (visible) shown += 1;
    }
    count.textContent = String(shown);
    empty.hidden = shown !== 0;
  };
  const clear = (): void => {
    search.value = '';
    status.value = 'all';
    model.value = 'all';
    apply();
    search.focus();
  };
  listen(search, 'input', apply, signal);
  listen(status, 'change', apply, signal);
  listen(model, 'change', apply, signal);
  listen(
    root,
    'click',
    (event) => {
      const target = event.target as Element;
      if (target.closest('[data-clear-requests]')) clear();
      const button = target.closest<HTMLButtonElement>('[data-request-item]');
      if (!button) return;
      const request = requestRecords.find(
        (candidate) => candidate.id === button.dataset.requestItem,
      );
      if (!request) return;
      for (const row of rows) row.classList.toggle('is-selected', row.contains(button));
      detail.innerHTML = renderRequestDetail(request);
    },
    signal,
  );
}

const pageRenderers: Record<PageId, () => string> = {
  overview: renderOverview,
  models: renderModels,
  'model-detail': renderModelDetail,
  playground: renderPlayground,
  deploy: renderDeploy,
  endpoint: renderEndpoint,
  evaluations: renderEvaluations,
  requests: renderRequests,
  usage: renderUsage,
};

function renderPage(page: PageId, focusMain = false): void {
  pageController.abort();
  pageController = new AbortController();
  const main = getElement<HTMLElement>(app, '#main-content');
  main.innerHTML = pageRenderers[page]();
  const meta = pages.find((candidate) => candidate.id === page);
  document.title = `${meta?.shortLabel ?? 'Aster'} · Aster MaaS`;
  const active = page === 'model-detail' ? 'models' : page === 'deploy' ? 'endpoint' : page;
  for (const link of app.querySelectorAll<HTMLAnchorElement>('[data-nav-item]')) {
    if (link.dataset.navItem === active) link.setAttribute('aria-current', 'page');
    else link.removeAttribute('aria-current');
  }
  initTabs(main, pageController.signal);
  if (page === 'overview') initOverview(main, pageController.signal);
  if (page === 'models') initModels(main, pageController.signal);
  if (page === 'playground') initPlayground(main, pageController.signal);
  if (page === 'deploy') initDeploy(main, pageController.signal);
  if (page === 'endpoint') initEndpoint(main, pageController.signal);
  if (page === 'evaluations') initEvaluations(main, pageController.signal);
  if (page === 'requests') initRequests(main, pageController.signal);
  if (focusMain) {
    window.scrollTo({ top: 0, behavior: 'auto' });
    main.focus({ preventScroll: true });
  }
}

renderShell();
initShell();
renderPage(readPage(window.location.search));
